import { CATEGORY_MEANING, type AuthDecision } from '../engine/auth'
import type { Party, WorldEvent } from '../engine/events'
import { formatMoney, parseMoney } from '../engine/money'
import { apply, type Action, type Applied } from '../engine/reducer'
import {
  HOLD_EXPIRY_DAYS,
  availableMinor,
  dueForSettlement,
  heldMinor,
  inFlightToMerchantMinor,
  issuerAvailableMinor,
  merchantReceivedMinor,
  openHolds,
  settledGrossMinor,
  totalFeesMinor,
  unadvisedHeldMinor,
} from '../engine/select'
import type { Card, CardState, Channel, World } from '../engine/types'
import { initialWorld } from '../engine/world'
import {
  DECIDED_BY_LABEL,
  DECIDED_BY_PLAIN,
  DEPTH_BLURB,
  DEPTH_NAMES,
  RESPONSE_CODES,
  STEP_LABEL,
  describeEvent,
  moneyNote,
  type Depth,
} from './describe'
import { diagramSvg, edgeId, markerPos } from './diagram'
import { authFrames, settlementFrames, type Frame } from './frames'

const CHANNELS: { value: Channel; label: string }[] = [
  { value: 'card-present', label: 'In person, chip and PIN' },
  { value: 'contactless', label: 'In person, contactless' },
  { value: 'card-not-present', label: 'Online checkout' },
  { value: 'atm', label: 'Cash machine' },
]

const TRANSITIONS: Record<CardState, { to: CardState; label: string; reason: string }[]> = {
  created: [{ to: 'unactivated', label: 'Issue card', reason: 'Credential produced.' }],
  unactivated: [{ to: 'active', label: 'Activate', reason: 'Activated by the cardholder.' }],
  active: [
    { to: 'suspended', label: 'Freeze', reason: 'Frozen by the cardholder in the app.' },
    { to: 'closed', label: 'Close', reason: 'Closed permanently.' },
  ],
  suspended: [
    { to: 'active', label: 'Unfreeze', reason: 'Unfrozen by the cardholder.' },
    { to: 'closed', label: 'Close', reason: 'Closed permanently.' },
  ],
  expired: [{ to: 'closed', label: 'Close', reason: 'Closed after lapsing.' }],
  closed: [],
}

const TARGET_LABEL: Record<CardState, string> = {
  created: 'Create',
  unactivated: 'Issue',
  active: 'Activate',
  suspended: 'Freeze',
  expired: 'Lapse',
  closed: 'Close',
}

/** Which part of Visa handled each pre-issuer check. */
const STAGE_LABEL: Record<'routing' | 'vrm' | 'stip', string> = {
  routing: 'VisaNet',
  vrm: 'VRM',
  stip: 'STIP',
}

type Phase = 'idle' | 'auth' | 'settlement'

interface UiState {
  world: World
  log: WorldEvent[]
  depth: Depth
  decision?: AuthDecision
  frames: Frame[]
  cursor: number
  phase: Phase
  /** Committed only when the last frame is reached, so playback is not spoiled. */
  pending: Applied | null
  playing: boolean
  playTimer: number | null
  message: string
}

const state: UiState = {
  world: initialWorld(),
  log: [],
  depth: 1,
  frames: [],
  cursor: -1,
  phase: 'idle',
  pending: null,
  playing: false,
  playTimer: null,
  message: '',
}

let diagramDepth: Depth | null = null

const esc = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!)

const $ = (id: string) => document.getElementById(id)!

function commit(action: Action) {
  const result = apply(state.world, action)
  state.world = result.world
  state.log = [...state.log, ...result.events]
  return result
}

// ---------------------------------------------------------------------------
// Diagram
// ---------------------------------------------------------------------------

function ensureDiagram() {
  if (diagramDepth === state.depth) return
  $('diagram').innerHTML = diagramSvg(state.depth)
  diagramDepth = state.depth
}

const ALL_PARTIES: Party[] = ['cardholder', 'merchant', 'acquirer', 'scheme', 'issuer']

function paintDiagram() {
  ensureDiagram()
  const frame = state.frames[state.cursor]
  const marker = document.getElementById('dg-marker')
  const markerText = document.getElementById('dg-marker-text')
  if (!marker || !markerText) return

  for (const p of ALL_PARTIES) {
    const el = document.getElementById(`node-${p}`)
    if (!el) continue
    el.classList.toggle('lit', !!frame?.lit.includes(p))
    el.classList.toggle('deciding', frame?.deciding === p)
    el.classList.toggle('down', frame?.down === p)
  }

  document.querySelectorAll('.dg-edge').forEach((e) => e.classList.remove('active'))
  if (frame?.activeEdge) {
    document.getElementById(edgeId(frame.activeEdge[0], frame.activeEdge[1]))?.classList.add('active')
  }

  if (!frame) {
    marker.setAttribute('opacity', '0')
    return
  }
  const pos = markerPos(frame.marker.at)
  marker.setAttribute('opacity', '1')
  marker.setAttribute('transform', `translate(${pos.x}, ${pos.y})`)
  marker.classList.toggle('money', frame.marker.variant === 'money')
  markerText.textContent = frame.marker.text
}

// ---------------------------------------------------------------------------
// Panels
// ---------------------------------------------------------------------------

function renderStepper() {
  const frame = state.frames[state.cursor]
  const total = state.frames.length
  const atEnd = state.cursor >= total - 1
  const has = total > 0

  $('stepper').innerHTML = `
    <div class="btn-row">
      <button class="btn ghost" id="step-back" ${!has || state.cursor <= 0 ? 'disabled' : ''}>Back</button>
      <button class="btn" id="step-fwd" ${!has || atEnd ? 'disabled' : ''}>Step</button>
      <button class="btn ghost" id="step-play" ${!has || atEnd ? 'disabled' : ''}>${state.playing ? 'Pause' : 'Play'}</button>
      <button class="btn ghost" id="step-end" ${!has || atEnd ? 'disabled' : ''}>Skip to end</button>
      <span class="step-count">${has ? `${state.cursor + 1} of ${total}` : 'no run yet'}</span>
    </div>`

  $('flow-clock').textContent =
    frame?.clockMs !== undefined ? `+${frame.clockMs} ms` : state.phase === 'settlement' ? 'settlement' : ''
  $('flow-message').innerHTML = frame
    ? esc(frame.caption)
    : `<span class="idle">${esc(state.message || 'Present the card, then step through it one hop at a time.')}</span>`
}

function renderDecision() {
  const { decision, depth } = state
  const frame = state.frames[state.cursor]
  const host = $('decision')

  if (!decision) {
    host.innerHTML =
      '<p class="log-empty">Run a transaction. VisaNet looks first, and it does not always pass the message on.</p>'
    return
  }

  const schemeShown = frame?.schemeShown ?? decision.schemeChecks.length
  const issuerShown = frame?.issuerShown ?? decision.issuerChecks.length

  const schemeRows = decision.schemeChecks
    .slice(0, schemeShown)
    .map((c) => {
      const cls = c.passed ? 'passed' : c.responseCode ? 'failed' : 'warn'
      const mark = c.passed ? 'pass' : c.responseCode ? `refused ${c.responseCode}` : 'stand-in'
      const question = depth === 0 ? c.plain.question : c.question
      const detail = depth === 0 ? c.plain.detail : c.detail
      return `
      <div class="rung ${cls}">
        <div class="rung-index">${esc(STAGE_LABEL[c.stage])}</div>
        <div>
          <div class="rung-question">${esc(question)}</div>
          <div class="rung-detail">${esc(detail)}</div>
        </div>
        <div class="rung-mark">${esc(mark)}</div>
      </div>`
    })
    .join('')

  const allSteps = ['technical', 'card-state', 'controls', 'fraud', 'funds'] as const
  const issuerRows = decision.issuerBypassed
    ? `<div class="rung skipped">
         <div class="rung-index">—</div>
         <div>
           <div class="rung-question">The issuer never saw this transaction</div>
           <div class="rung-detail">${esc(depth === 0 ? DECIDED_BY_PLAIN[decision.decidedBy] : DECIDED_BY_LABEL[decision.decidedBy] + '.')}</div>
         </div>
         <div class="rung-mark">bypassed</div>
       </div>`
    : allSteps
        .map((step, i) => {
          const check = decision.issuerChecks.slice(0, issuerShown).find((c) => c.step === step)
          if (!check) {
            const reached = decision.issuerChecks.some((c) => c.step === step)
            const done = issuerShown >= decision.issuerChecks.length
            const cls = !reached && done ? 'skipped' : 'pending'
            return `
            <div class="rung ${cls}">
              <div class="rung-index">${i + 1}</div>
              <div><div class="rung-question">${esc(STEP_LABEL[step])}</div></div>
              <div class="rung-mark">${cls === 'skipped' ? 'not reached' : ''}</div>
            </div>`
          }
          const cls = check.passed ? 'passed' : 'failed'
          const mark = check.passed ? 'pass' : `refused ${check.responseCode}`
          const detail =
            depth === 2 && !check.passed ? `${check.detail} DE39 ${check.responseCode}.` : check.detail
          return `
          <div class="rung ${cls}">
            <div class="rung-index">${i + 1}</div>
            <div>
              <div class="rung-question">${esc(depth === 0 ? check.question : STEP_LABEL[step])}</div>
              <div class="rung-detail">${esc(detail)}</div>
            </div>
            <div class="rung-mark">${esc(mark)}</div>
          </div>`
        })
        .join('')

  host.innerHTML = `
    <div class="decision-group">
      <div class="decision-head">At VisaNet<span>before the issuer is asked</span></div>
      ${schemeRows || '<div class="hold-empty">Nothing yet.</div>'}
    </div>
    <div class="decision-group">
      <div class="decision-head">At the issuer<span>in order, first refusal wins</span></div>
      ${issuerRows}
    </div>`
}

function renderReceipt() {
  const { decision, depth } = state
  const host = $('receipt')
  const atEnd = state.frames.length > 0 && state.cursor >= state.frames.length - 1
  if (!decision || !atEnd || state.phase !== 'auth') {
    host.innerHTML = ''
    return
  }
  const code = RESPONSE_CODES[decision.responseCode]
  const approved = decision.outcome !== 'declined'
  const headline = approved
    ? depth === 0
      ? `Accepted, ${formatMoney(decision.amountApprovedMinor)}`
      : `Approved ${formatMoney(decision.amountApprovedMinor)}`
    : depth === 0
      ? code.cardholderSaw
      : code.name

  const who = depth === 0 ? DECIDED_BY_PLAIN[decision.decidedBy] : `${DECIDED_BY_LABEL[decision.decidedBy]}.`
  const cat =
    decision.category !== undefined
      ? `<div class="category">Category ${decision.category}. ${esc(CATEGORY_MEANING[decision.category])}</div>`
      : ''

  host.innerHTML = `
    <div class="receipt ${approved ? 'approved' : 'declined'}">
      <div class="receipt-code">${esc(decision.responseCode)}</div>
      <div class="receipt-text">${esc(headline)}<small>${esc(who)}</small>${cat}</div>
    </div>`
}

function renderMoney() {
  const w = state.world
  const { depth } = state
  const ledger = w.account.ledgerMinor
  const issuerView = issuerAvailableMinor(w)
  const actual = availableMinor(w)
  const unadvised = unadvisedHeldMinor(w)

  const labels =
    depth === 0
      ? {
          ledger: ['Money in the account', 'What has actually been taken out'],
          available: ['Money you can spend', 'What the app shows you'],
          held: ['Set aside', 'Promised, not yet gone'],
          received: ['Reached the shop', 'Actually in the shop bank account'],
        }
      : {
          ledger: ['Ledger balance', 'Posted transactions only'],
          available: ['Available balance', 'Ledger less open holds'],
          held: ['Open holds', 'Authorised, not yet cleared'],
          received: ['Merchant funded', 'Settled and net of fees'],
        }

  const row = (label: string[], value: number, cls = '') => `
    <div class="money-row ${cls}">
      <div class="money-label">${esc(label[0]!)}<small>${esc(label[1]!)}</small></div>
      <div class="money-value">${formatMoney(value)}</div>
    </div>`

  const holds = openHolds(w)
  const holdRows = holds.length
    ? holds
        .map(
          (t) => `
      <div class="hold-row">
        <span>${esc(t.merchant.name)}${t.advised ? '' : ' <em>stand-in</em>'}<br><span class="log-day">day ${t.hold!.placedDay}, lapses day ${t.hold!.expiresDay}</span></span>
        <span class="mono">${formatMoney(t.hold!.amountMinor)}</span>
      </div>`,
        )
        .join('')
    : '<div class="hold-empty">No open holds.</div>'

  const divergence =
    unadvised > 0
      ? `<div class="alert">
           <strong>${esc(depth === 0 ? 'Your bank is working from the wrong number.' : 'The issuer position is stale.')}</strong>
           ${esc(
             depth === 0
               ? `Visa approved ${formatMoney(unadvised)} while your bank was unreachable. Until it is told, your bank thinks you have ${formatMoney(issuerView)} but you really have ${formatMoney(actual)}.`
               : `${formatMoney(unadvised)} approved in stand-in and not yet advised. Issuer believes available is ${formatMoney(issuerView)}; the true figure is ${formatMoney(actual)}, and the issuer is still authorising against the former.`,
           )}
           <div class="btn-row" style="margin-top:0.6rem"><button class="btn" id="advice">Deliver the advice messages</button></div>
         </div>`
      : ''

  $('money').innerHTML = `
    ${row(labels.ledger, ledger, 'headline')}
    ${row(labels.available, actual, 'headline')}
    ${unadvised > 0 ? row(depth === 0 ? ["What your bank thinks you have", 'Wrong until the advice lands'] : ['Issuer view of available', 'Excludes unadvised stand-in holds'], issuerView, 'gap') : ''}
    ${row(labels.held, heldMinor(w), heldMinor(w) > 0 ? 'gap' : '')}
    ${row(labels.received, merchantReceivedMinor(w))}
    ${inFlightToMerchantMinor(w) > 0 ? row(depth === 0 ? ['Left your account, not arrived', 'Waiting for the banks to square up'] : ['Cleared, not settled', 'Posted, awaiting settlement'], inFlightToMerchantMinor(w)) : ''}
    ${divergence}
    <div class="holds">${holdRows}</div>`

  $('money-note').textContent = moneyNote(depth)
}

function renderSettlement() {
  const w = state.world
  const { depth } = state
  const due = dueForSettlement(w)
  const gross = settledGrossMinor(w)
  const fees = totalFeesMinor(w)
  const net = merchantReceivedMinor(w)

  const legs = w.settlements.length
    ? (() => {
        const ic = w.settlements.reduce((s, x) => s + x.interchangeMinor, 0)
        const sf = w.settlements.reduce((s, x) => s + x.schemeFeeMinor, 0)
        const am = w.settlements.reduce((s, x) => s + x.acquirerMarginMinor, 0)
        const leg = (from: string, to: string, amount: number, note: string) => `
          <div class="leg">
            <div class="leg-route">${esc(from)} <span>→</span> ${esc(to)}</div>
            <div class="leg-amount mono">${formatMoney(amount)}</div>
            <div class="leg-note">${esc(note)}</div>
          </div>`
        return `
          ${leg('Cardholder', 'Issuer', gross, depth === 0 ? 'What you were charged' : 'Gross, posted at clearing')}
          ${leg('Issuer', 'Visa', w.settlements.reduce((s, x) => s + x.issuerPaysMinor, 0), `Issuer keeps ${formatMoney(ic)} interchange reimbursement fee`)}
          ${leg('Visa', 'Acquirer', w.settlements.reduce((s, x) => s + x.schemePaysMinor, 0), `Visa keeps ${formatMoney(sf)} in scheme fees`)}
          ${leg('Acquirer', 'Merchant', net, `Acquirer keeps ${formatMoney(am)} margin`)}`
      })()
    : '<div class="hold-empty">Nothing settled yet.</div>'

  $('settlement').innerHTML = `
    <div class="panel-body">
      <div class="btn-row">
        <button class="btn" id="settle" ${due.length ? '' : 'disabled'}>Run settlement${due.length ? ` (${due.length})` : ''}</button>
        <button class="btn ghost" id="replay-settlement" ${w.settlements.length ? '' : 'disabled'}>Watch the money move</button>
      </div>
      <p class="note" style="margin-top:0.7rem">${esc(
        depth === 0
          ? 'This is the only step where money genuinely moves, and it moves between banks through Visa. You are not involved, and the shop does not get what you paid.'
          : 'Settlement runs through the Visa Settlement Service, issuer to Visa to acquirer to merchant, net of the interchange reimbursement fee and Visa fees. It is separate from clearing and happens a day or more later.',
      )}</p>
      <p class="note caveat">Rates here are illustrative. South African interchange is regulated and published by the SARB, and real rates vary by card type, channel and merchant category.</p>
    </div>
    <div class="legs">${legs}</div>
    ${
      w.settlements.length
        ? `<div class="money-row"><div class="money-label">Taken in fees<small>Out of ${formatMoney(gross)} charged</small></div><div class="money-value">${formatMoney(fees)}</div></div>`
        : ''
    }`
}

function renderNetwork() {
  const cfg = state.world.config
  const { depth } = state

  const rules = cfg.vrmRules
    .map(
      (r) => `
      <label class="toggle">
        <input type="checkbox" data-vrm="${esc(r.id)}" ${r.enabled ? 'checked' : ''}>
        <span><strong>${esc(r.label)}</strong><small>${esc(r.description)}</small></span>
      </label>`,
    )
    .join('')

  $('network').innerHTML = `
    <label class="toggle">
      <input type="checkbox" id="issuer-reachable" ${cfg.issuerReachable ? 'checked' : ''}>
      <span><strong>Issuer host reachable</strong><small>${esc(
        depth === 0
          ? 'Turn this off to see what happens when your bank cannot be reached.'
          : 'Turn this off to force Visa Stand-In Processing to answer for the issuer.',
      )}</small></span>
    </label>
    <label class="toggle">
      <input type="checkbox" id="stip-enabled" ${cfg.stip.enabled ? 'checked' : ''}>
      <span><strong>Stand-In Processing (STIP)</strong><small>Visa answers on the issuer's behalf, up to ${formatMoney(cfg.stip.limitMinor)}, then sends 0120 advices so the issuer can catch up.</small></span>
    </label>
    <div class="toggle-group-head">Visa Risk Manager rules</div>
    ${rules}
    <p class="note" style="margin-top:0.7rem">${esc(
      depth === 0
        ? 'These rules belong to your bank, but Visa runs them. A payment can be refused without your bank ever hearing about it.'
        : 'These are issuer-configured rules executed at VisaNet. A decline here never reaches the issuer host, and the issuer only learns of it from reporting.',
    )}</p>`
}

function cardPanel(card: Card) {
  const { depth } = state
  const pan =
    depth === 2 ? card.pan.replace(/(.{4})/g, '$1 ').trim() : `${'•••• '.repeat(3)}${card.pan.slice(-4)}`
  const controls = card.controls
  const buttons = TRANSITIONS[card.state]
  const allStates: CardState[] = ['unactivated', 'active', 'suspended', 'closed']

  return `
    <div class="card-face">
      <div class="pan">${esc(pan)}</div>
      <div class="card-meta">
        <div><span>Expires</span><span class="mono">${String(card.expiry.month).padStart(2, '0')}/${String(card.expiry.year).slice(-2)}</span></div>
        <div><span>Form</span><span class="mono">${esc(card.formFactor)}</span></div>
        <div><span>State</span><span class="state-pill ${card.state}">${esc(card.state)}</span></div>
      </div>
    </div>
    <div class="panel-body">
      <div class="btn-row" style="margin-bottom:0.9rem">
        ${allStates
          .map((s) => {
            const t = buttons.find((b) => b.to === s)
            return `<button class="btn ghost" data-state="${s}" data-reason="${esc(t?.reason ?? '')}" ${t ? '' : 'disabled'}>${esc(t?.label ?? TARGET_LABEL[s])}</button>`
          })
          .join('')}
      </div>
      <dl class="kv">
        <dt>Per transaction</dt><dd>${formatMoney(controls.perTransactionLimitMinor ?? 0)}</dd>
        <dt>Per day</dt><dd>${formatMoney(controls.dailyLimitMinor ?? 0)}</dd>
        ${depth === 2 ? `<dt>Card ID</dt><dd>${esc(card.id)}</dd><dt>PAR</dt><dd style="word-break:break-all">${esc(card.par)}</dd>` : ''}
      </dl>
    </div>`
}

function renderCard() {
  const card = state.world.cards[0]
  if (card) $('card').innerHTML = cardPanel(card)
}

function renderTerminal() {
  const merchants = state.world.merchants
    .map((m) => `<option value="${esc(m.id)}">${esc(m.name)} (MCC ${esc(m.mcc)})</option>`)
    .join('')
  const channels = CHANNELS.map((c) => `<option value="${esc(c.value)}">${esc(c.label)}</option>`).join('')

  $('terminal').innerHTML = `
    <div class="field">
      <label class="field-label" for="merchant">Merchant</label>
      <select id="merchant">${merchants}</select>
    </div>
    <div class="field">
      <label class="field-label" for="amount">Amount</label>
      <input type="text" id="amount" value="248.00" inputmode="decimal" autocomplete="off">
    </div>
    <div class="field">
      <label class="field-label" for="channel">How the card is used</label>
      <select id="channel">${channels}</select>
    </div>
    <div class="btn-row">
      <button class="btn" id="present">Present card</button>
      <button class="btn ghost" id="reset">Reset</button>
    </div>`
}

function renderClock() {
  const w = state.world
  const pending = w.transactions.filter((t) => t.outcome !== 'declined' && t.clearings.length === 0)

  const rows = pending.length
    ? pending
        .map(
          (t) => `
      <div class="hold-row">
        <span>${esc(t.merchant.name)}<br><span class="log-day">authorised ${formatMoney(t.amountApprovedMinor)} on day ${t.day}${t.hold?.status === 'expired' ? ', hold lapsed' : ''}</span></span>
        <span class="nowrap">
          <input type="text" class="clear-amount" data-txn="${esc(t.id)}" value="${(t.amountApprovedMinor / 100).toFixed(2)}" size="6">
          <button class="btn ghost clear-btn" data-txn="${esc(t.id)}">Clear</button>
        </span>
      </div>`,
        )
        .join('')
    : '<div class="hold-empty">Nothing waiting to clear.</div>'

  $('clock').innerHTML = `
    <div class="money-row">
      <div class="money-label">Day<small>Authorisation, clearing and settlement sit days apart</small></div>
      <div class="money-value">${w.day}</div>
    </div>
    <div class="panel-body">
      <div class="btn-row"><button class="btn ghost" id="advance">Advance a day</button></div>
      <p class="note" style="margin-top:0.7rem">An uncleared hold lapses after ${HOLD_EXPIRY_DAYS} days. A clearing that arrives after that still posts.</p>
    </div>
    <div class="holds">${rows}</div>`
}

function renderLog() {
  const entries = [...state.log].reverse()
  $('log').innerHTML = entries.length
    ? entries
        .map(
          (e) => `
      <div class="log-entry">
        <span class="log-seq">${e.seq}</span>
        <span class="log-day">d${e.day}</span>
        <span>${esc(describeEvent(e, state.depth))}</span>
      </div>`,
        )
        .join('')
    : '<div class="log-empty">Nothing has happened yet.</div>'
}

function renderDepth() {
  $('depth-options').innerHTML = ([0, 1, 2] as Depth[])
    .map(
      (d) =>
        `<button data-depth="${d}" aria-pressed="${d === state.depth}" aria-label="${esc(DEPTH_NAMES[d])} depth. ${esc(DEPTH_BLURB[d])}" title="${esc(DEPTH_BLURB[d])}">${esc(DEPTH_NAMES[d])}</button>`,
    )
    .join('')
}

function render() {
  renderDepth()
  paintDiagram()
  renderStepper()
  renderDecision()
  renderReceipt()
  renderMoney()
  renderSettlement()
  renderNetwork()
  renderCard()
  renderClock()
  renderLog()
}

/** The terminal form is preserved across re-renders so typing is not lost. */
function renderAll() {
  const merchant = (document.getElementById('merchant') as HTMLSelectElement | null)?.value
  const amount = (document.getElementById('amount') as HTMLInputElement | null)?.value
  const channel = (document.getElementById('channel') as HTMLSelectElement | null)?.value
  render()
  renderTerminal()
  if (merchant) (document.getElementById('merchant') as HTMLSelectElement).value = merchant
  if (amount) (document.getElementById('amount') as HTMLInputElement).value = amount
  if (channel) (document.getElementById('channel') as HTMLSelectElement).value = channel
}

// ---------------------------------------------------------------------------
// Stepping
// ---------------------------------------------------------------------------

function stopPlaying() {
  if (state.playTimer !== null) clearInterval(state.playTimer)
  state.playTimer = null
  state.playing = false
}

function goTo(index: number) {
  const clamped = Math.max(0, Math.min(index, state.frames.length - 1))
  state.cursor = clamped

  // The world is committed the moment the run finishes, never before.
  if (clamped === state.frames.length - 1 && state.pending) {
    state.world = state.pending.world
    state.log = [...state.log, ...state.pending.events]
    state.pending = null
    stopPlaying()
    renderAll()
    return
  }
  paintDiagram()
  renderStepper()
  renderDecision()
  renderReceipt()
}

function play() {
  if (state.playing) return stopPlaying(), renderStepper()
  state.playing = true
  renderStepper()
  state.playTimer = window.setInterval(() => {
    if (state.cursor >= state.frames.length - 1) {
      stopPlaying()
      renderStepper()
      return
    }
    goTo(state.cursor + 1)
  }, 900)
}

function startAuthorisation() {
  stopPlaying()
  const merchantId = (document.getElementById('merchant') as HTMLSelectElement).value
  const channel = (document.getElementById('channel') as HTMLSelectElement).value as Channel
  const amountMinor = parseMoney((document.getElementById('amount') as HTMLInputElement).value)
  const card = state.world.cards[0]
  if (amountMinor === null || amountMinor <= 0 || !card) {
    state.message = 'Enter an amount such as 248.00.'
    renderStepper()
    return
  }

  const result = apply(state.world, {
    type: 'authorise',
    request: {
      cardId: card.id,
      merchantId,
      channel,
      amountMinor,
      credential: { kind: 'pan', cardId: card.id, cryptogramPresent: false },
    },
  })

  state.decision = result.decision!
  state.pending = result
  state.phase = 'auth'
  state.frames = authFrames(result.decision!, amountMinor, state.depth)
  state.cursor = 0
  renderAll()
}

function watchSettlement() {
  stopPlaying()
  if (state.world.settlements.length === 0) return
  state.phase = 'settlement'
  state.frames = settlementFrames(state.world.settlements, state.depth)
  state.cursor = 0
  state.pending = null
  renderAll()
}

// ---------------------------------------------------------------------------
// Wiring
// ---------------------------------------------------------------------------

export function mount() {
  render()
  renderTerminal()

  document.addEventListener('change', (ev) => {
    const el = ev.target as HTMLInputElement
    if (el.id === 'issuer-reachable') {
      commit({ type: 'set-issuer-reachable', reachable: el.checked })
      renderAll()
    } else if (el.id === 'stip-enabled') {
      commit({ type: 'set-stip-enabled', enabled: el.checked })
      renderAll()
    } else if (el.dataset.vrm) {
      commit({ type: 'set-vrm-rule', ruleId: el.dataset.vrm, enabled: el.checked })
      renderAll()
    }
  })

  document.addEventListener('click', (ev) => {
    const el = (ev.target as HTMLElement).closest('button')
    if (!el) return

    if (el.dataset.depth) {
      state.depth = Number(el.dataset.depth) as Depth
      diagramDepth = null
      // Rebuild frames at the new depth so the captions match.
      if (state.decision && state.phase === 'auth') {
        const amount = state.pending
          ? state.pending.world.transactions.at(-1)!.amountRequestedMinor
          : state.world.transactions.at(-1)?.amountRequestedMinor ?? 0
        state.frames = authFrames(state.decision, amount, state.depth)
      } else if (state.phase === 'settlement') {
        state.frames = settlementFrames(state.world.settlements, state.depth)
      }
      renderAll()
      return
    }

    switch (el.id) {
      case 'present':
        return startAuthorisation()
      case 'step-fwd':
        return goTo(state.cursor + 1)
      case 'step-back':
        return goTo(state.cursor - 1)
      case 'step-play':
        return play()
      case 'step-end':
        stopPlaying()
        return goTo(state.frames.length - 1)
      case 'advance':
        stopPlaying()
        commit({ type: 'advance-day' })
        return renderAll()
      case 'settle':
        stopPlaying()
        commit({ type: 'settle' })
        watchSettlement()
        return
      case 'replay-settlement':
        return watchSettlement()
      case 'advice':
        stopPlaying()
        commit({ type: 'deliver-advice' })
        return renderAll()
      case 'reset':
        stopPlaying()
        state.world = initialWorld()
        state.log = []
        delete state.decision
        state.frames = []
        state.cursor = -1
        state.phase = 'idle'
        state.pending = null
        state.message = ''
        diagramDepth = null
        render()
        renderTerminal()
        return
    }

    if (el.dataset.state) {
      const card = state.world.cards[0]
      if (!card) return
      commit({
        type: 'set-card-state',
        cardId: card.id,
        state: el.dataset.state as CardState,
        reason: el.dataset.reason || '',
      })
      return renderAll()
    }

    if (el.classList.contains('clear-btn')) {
      const txn = el.dataset.txn!
      const input = document.querySelector<HTMLInputElement>(`.clear-amount[data-txn="${txn}"]`)
      const amountMinor = parseMoney(input?.value ?? '')
      if (amountMinor === null || amountMinor <= 0) return
      commit({ type: 'clear', transactionId: txn, amountMinor })
      renderAll()
    }
  })
}
